/**
 * The eyebrow above the "next meal" hero card (#579): "Aujourd'hui · Souper",
 * "Demain · Dîner", "Samedi · Souper".
 *
 * Deliberately two reused parts rather than one written-out phrase. "Ce soir" /
 * "Ce midi" / "Demain matin" would need a hand-written string per (day × meal)
 * pair in every locale, and Quebec French already overloads déjeuner/dîner/souper
 * against France French — so a phrase table is exactly the kind of thing that
 * silently drifts. Day key + meal key both already exist and stay correct.
 *
 * Pure `.ts` so Jest can import it (tsconfig emits `jsx: preserve`, which makes
 * `.tsx` unimportable from tests).
 */

import { dayFullKey } from './constants';

const MEAL_KEYS = ['breakfast', 'lunch', 'dinner'] as const;

export interface EyebrowKeys {
  /** i18n key for the day part — "Aujourd'hui", "Demain", or a weekday name. */
  dayKey: string;
  /** i18n key for the meal part — déjeuner / dîner / souper. */
  mealKey: string;
}

/**
 * @param targetDi Day index of the next meal, Monday = 0.
 * @param todayDi  Day index of today, Monday = 0.
 * @param mi       Meal index — 0 breakfast, 1 lunch, 2 dinner.
 */
export function nextMealEyebrowKeys(
  targetDi: number,
  todayDi: number,
  mi: number,
): EyebrowKeys {
  const mealKey = `thisWeek.mealTypes.${MEAL_KEYS[mi] ?? 'dinner'}`;

  if (targetDi === todayDi) return { dayKey: 'thisWeek.today', mealKey };
  if (targetDi === todayDi + 1) return { dayKey: 'thisWeek.tomorrow', mealKey };
  return { dayKey: dayFullKey(targetDi), mealKey };
}

/**
 * Whether the hero and the desktop bar offer « Cuisiner »: only for a meal
 * later today. The next meal can be days away (nothing left tonight, the next
 * one Saturday), and a button to start cooking it then reads as an order.
 * The card itself still opens the recipe.
 */
export function offersCookNow(targetDi: number, todayDi: number): boolean {
  return targetDi === todayDi;
}
